import axios from 'axios';
import React from 'react';
import { enqueueSnackbar } from 'notistack';
import { ServiceModel } from 'src/models/services/service';
import { API_url } from '../api.setup';

const API_Controller = '/services';

export const ServicesService = {
  getAllServices: async () => {
    try {
      const response = await axios.get(`${API_url}${API_Controller}/getall`);
      return response.data as ServiceModel[];
    } catch (error) {
      throw error;
    }
  },

  getService: async (serviceID) => {
    try {
      const response = await axios.get(`${API_url}${API_Controller}/get?id=${serviceID}`);
      return response.data as ServiceModel;
    } catch (error) {
      console.error(error.response.data);
    }
  },

  addService: async (dataToUpdate) => {
    try {
      const reqModel = {
        name: dataToUpdate.name,
        subTitle: dataToUpdate.subTitle,
        featureHighlight: dataToUpdate.featureHighlight,
        shortDescription: dataToUpdate.shortDescription,
        requirementFromCustomer: dataToUpdate.requirementFromCustomer,
        detailedDescription: dataToUpdate.detailedDescription,
        feature1: dataToUpdate.feature1,
        feature1Description: dataToUpdate.feature1Description,
        feature2: dataToUpdate.feature2,
        feature2Description: dataToUpdate.feature2Description,
        timeRequired: dataToUpdate.timeRequired,
        status: dataToUpdate.status,
        averageRating: dataToUpdate.averageRating,
        isBestSeller: dataToUpdate.isBestSeller || false,
        isComingSoon: dataToUpdate.isComingSoon,
        onlyBackend: dataToUpdate.onlyBackend,
        serviceCategoryId: dataToUpdate.serviceCategory.id,
        serviceTypeId: dataToUpdate.serviceType.id,
        serviceSOPs: dataToUpdate.serviceSOPs.map((x) => x.id),
        serviceMaterials: dataToUpdate.serviceMaterials.map((x) => ({
          materialId: x.id,
          count: x.count
        }))
      };

      const response = await axios.post(
        `${API_url}${API_Controller}`,
        reqModel
      );
      enqueueSnackbar(`Service "${dataToUpdate.name}" Added Successfully`, {
        variant: 'success'
      });
      return response.data as ServiceModel;
    } catch (error) {
      enqueueSnackbar(`Service "${dataToUpdate.name}" can't be added`, {
        variant: 'error'
      });
      throw error;
    }
  },

  deleteService: async (DeleteId) => {
    try {
      const response = await axios.delete(
        `${API_url}${API_Controller}/delete?id=${DeleteId}`
      );
      enqueueSnackbar(`Service deleted Successfully`, {
        variant: 'success'
      });
      return response.data as ServiceModel;
    } catch (error) {
      enqueueSnackbar(`Service can't be delete. Assigned to Booking order`, {
        variant: 'error'
      });
      console.error('Error deleting data:', error);
      throw error;
    }
  },

  editService: async (editedData) => {
    try {
      const reqModel = {
        id: editedData.id,
        name: editedData.name,
        subTitle: editedData.subTitle,
        featureHighlight: editedData.featureHighlight,
        shortDescription: editedData.shortDescription,
        requirementFromCustomer: editedData.requirementFromCustomer,
        detailedDescription: editedData.detailedDescription,
        feature1: editedData.feature1,
        feature1Description: editedData.feature1Description,
        feature2: editedData.feature2,
        feature2Description: editedData.feature2Description,
        timeRequired: editedData.timeRequired ?? ' ',
        averageRating: editedData.averageRating ?? ' ',
        status: editedData.status,
        isBestSeller: editedData.isBestSeller,
        isComingSoon: editedData.isComingSoon,
        onlyBackend: editedData.onlyBackend,
        serviceCategoryId: editedData.serviceCategoryId,
        serviceTypeId: editedData.serviceTypeId,
        serviceSOPs: editedData.serviceSOPs,
        serviceMaterials: editedData.serviceMaterials.map((x) => ({
          materialId: x.materialId,
          count: x.count
        }))
      };

      const response = await axios.put(
        `${API_url}${API_Controller}/update`,
        reqModel
      );
      enqueueSnackbar(`Sercive " ${editedData.name} " Edited Successfully`, {
        variant: 'success'
      });

      return response.data as ServiceModel;
    } catch (error) {
      enqueueSnackbar(`Sercive " ${editedData.name} " Edited Unsuccessfully`, {
        variant: 'error'
      });
      throw error;
    }
  },
  uploadServiceImages: async (serviceId: number, files: File[]) => {
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      const response = await axios.post(
        `${API_url}${API_Controller}/uploadServiceImages?serviceId=${serviceId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error uploading images:', error);
      throw error;
    }
  },

  uploadServiceTitleImage: async (id, file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API_url}${API_Controller}/uploadTitleImage?serviceId=${id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error uploading title image:', error);
      enqueueSnackbar('Failed to upload title image', { variant: 'error' });
      throw error;
    }
  }
  ,

  getServiceImages: async (serviceId: number) => {
    try {
      const response = await axios.get(
        `${API_url}${API_Controller}/getServiceImages?serviceId=${serviceId}`
      );
      return response.data as string[];
    } catch (error) {
      enqueueSnackbar('Failed to fetch service images', {
        variant: 'error'
      });
      console.error('Error fetching service images:', error);
      throw error;
    }
  },

  deleteServiceImage: async (imageUrl: string) => {
    try {
      const response = await axios.delete(`${API_url}${API_Controller}/deleteServiceImage`, {
        params: { imageUrl }
      });
      return response.data;
    } catch (error) {
      console.error('Error delete service image:', error);
      throw error;
    }
  },

  assignServiceToCities: async (serviceId: number, cityMappings: any[]) => {
    try {
      const reqModel = {
        serviceId,
        cityMappings,
      };

      const response = await axios.put(
        `${API_url}${API_Controller}/assignServiceToCities`,
        reqModel
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getServiceCityMapping: async (serviceId: number) => {
    try {
      const response = await axios.get(
        `${API_url}${API_Controller}/GetServiceCityMappings?serviceId=${serviceId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching service city mappings:', error);
      throw error;
    }
  },
  updateServiceDisplayOrder: async (displayOrders: { id: number; displayOrder: number | null }[]) => {
    try {
      const response = await axios.put(
        `${API_url}${API_Controller}/updateServiceDisplayOrder`,
        displayOrders
      );
      enqueueSnackbar('Service order updated successfully', { variant: 'success' });
      return response.data;
    } catch (error) {
      enqueueSnackbar('Failed to update service order', { variant: 'error' });
      throw error;
    }
  },

  getServiceRecommendationMapping: async (serviceIds: number[]) => {
    try {
      const response = await axios.post(
        `${API_url}${API_Controller}/getServiceRecommendationForServices`,
        serviceIds
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching service city mappings:', error);
      throw error;
    }
  },
  addServiceRecommendationMapping: async (serviceId: number, recommendedServiceIds: number[]) => {
    try {
      console.log("rec", recommendedServiceIds);

      const response = await axios.put(
        `${API_url}${API_Controller}/addServiceRecommendationMappings?serviceId=${serviceId}`,
        recommendedServiceIds
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching service city mappings:', error);
      throw error;
    }
  },
};
